/**
 * The browser's half of the Contact us proof-of-work - the server's is
 * apps/api/src/contact-pow.ts, which explains the scheme and why it exists.
 *
 * Find the n in [0, maxNumber] where sha256(salt + n) is the challenge. WebCrypto, because it
 * is native in every browser this site supports and needs no dependency; in batches of 500
 * promises, which measured about twice as fast as awaiting one digest at a time (57,000 a
 * second against 30,000 on a desktop). Each batch awaits, so the page never freezes.
 */

export interface PowChallenge {
  readonly algorithm: 'SHA-256';
  readonly salt: string;
  readonly challenge: string;
  readonly maxNumber: number;
  readonly signature: string;
}

const BATCH = 500;

function hex(buf: ArrayBuffer): string {
  const b = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < b.length; i++) s += b[i]!.toString(16).padStart(2, '0');
  return s;
}

/** The number, or null if the search ran out (a challenge we did not issue) or was aborted. */
export async function solveChallenge(
  c: Pick<PowChallenge, 'salt' | 'challenge' | 'maxNumber'>, signal?: AbortSignal,
): Promise<number | null> {
  const enc = new TextEncoder();
  for (let start = 0; start <= c.maxNumber; start += BATCH) {
    if (signal?.aborted) return null;
    const end = Math.min(c.maxNumber, start + BATCH - 1);
    const jobs: Promise<ArrayBuffer>[] = [];
    for (let n = start; n <= end; n++) jobs.push(crypto.subtle.digest('SHA-256', enc.encode(`${c.salt}${n}`)));
    const hashes = await Promise.all(jobs);
    for (let i = 0; i < hashes.length; i++) {
      if (hex(hashes[i]!) === c.challenge) return start + i;
    }
  }
  return null;
}
